import React, { useEffect, useMemo, useState } from 'react'
import { Card, Chip, Grid, Stack, Tooltip, Typography } from '@mui/material'
import api from 'services/api'
import { SubHeader } from 'components/SubHeader'
import { useLocation } from 'react-router-dom'
import { Box } from '@mui/system'
import { useAppDispatch } from 'redux/hooks'
import { initialBreadcrumb } from 'redux/reducers/breadcrumb/actions'
import { useTranslation } from 'react-i18next'
import { DataGridHeader } from './components/DataGridHeader'
import { ContainerWorkout } from './styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Thumbnail } from '../../components/Thumbnail'
import { setIsVisible, setSelectedVideo } from '../../redux/reducers/modal-player/modal-player.actions'
import { LoadingPage } from 'components/LoadingPage'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { removeAccent } from '../../utils'
import useResponsive from 'hooks/useResponsive'

export type WorkoutProps = {
  id: string
  labels: string[]
  externalId: string
  name: string
  videoUrl: string
  thumbUrl: string
  type: string
}

export const Workouts = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [workoutList, setWorkoutList] = useState<WorkoutProps[]>([])
  const [workoutResult, setWorkoutResult] = useState<WorkoutProps[]>([])
  const [labels, setLabels] = useState<string[]>([])
  const mdUp = useResponsive('up', 'md')

  useEffect(() => {
    dataFetch().catch(console.error)
  }, [])

  const dataFetch = async () => {
    dispatch(initialBreadcrumb({ label: t('header.title.workouts'), path: location.pathname }))
    try {
      setLoading(true)
      const { data } = await api.get('workouts/defaults')
      const labels = data.flatMap(
        (workout: WorkoutProps) => workout.labels.map(label => label.charAt(0).toUpperCase() + label.slice(1))
      ).sort()
      const labelsUnique = labels.filter((item: string, index: number) => labels.indexOf(item) === index)
      setWorkoutList(data)
      setWorkoutResult(data)
      setLabels(labelsUnique)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (search: string, chosenLabels: string[]) => {
    if (!search && chosenLabels.length === 0) {
      setWorkoutResult(workoutList)
      return
    }
    setWorkoutResult([])
    const searchText = removeAccent(search)
    const dataFound = workoutList
      .filter((workout) => {
        const name = removeAccent(workout.name)
        return name.includes(searchText)
      })
      .filter((workout) => {
        if (chosenLabels.length === 0) return true
        return chosenLabels.every((item: string) =>
          workout.labels.map(label => removeAccent(label)).some(it => it.includes(removeAccent(item))))
      })
    setWorkoutResult(dataFound)
  }

  const showVideoModal = ({ videoUrl, id, name }: WorkoutProps) => {
    dispatch(setSelectedVideo({ videoUrl, id, name }))
    dispatch(setIsVisible(true))
  }

  const columns = useMemo<Array<GridColDef<WorkoutProps>>>(
    () => [
      {
        field: 'link',
        headerName: '',
        flex: 1,
        align: 'center',
        valueGetter: (params) => params.row.videoUrl,
        renderCell: (params) => {
          return <Thumbnail data={params.row} />
        }
      },
      {
        field: 'name',
        headerName: `${t('workout.title.columnName')}`,
        flex: 1,
        renderCell: (params) => {
          return <Box maxWidth={ '95%' } textAlign={ 'justify' }>
            {t(`workouts.${params.row.name}`, { defaultValue: params.row.name })}
          </Box>
        }
      },
      {
        field: 'type',
        headerName: `${t('workout.title.columnType')}`,
        flex: 1,
        valueGetter: (params) => t(`workout.title.row.${params.row.type.toLowerCase()}`)
      },
      {
        field: 'label',
        headerName: `${t('workout.title.columnLabels')}`,
        flex: 1,
        renderCell: (params) => {
          return <Tooltip arrow placement='top' title={`${params.row.labels?.join(',')}`}>
            <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {params.row.labels.map((label, index) =>
                <Chip label={label} sx={{ marginRight: '7px' }} color="primary" variant="outlined" key={index} />
              )}
            </Box>
          </Tooltip>
        }
      }
    ],
    [showVideoModal]
  )

  return (
    <Box>
      <SubHeader
        path={location.pathname}
        goBack={false}
      />
      <ContainerWorkout>
        {loading
          ? <LoadingPage/>
          : <Stack sx={{ width: '100%' }}>
            <Card sx={{ width: '100%' }} variant="outlined">
              <Stack spacing={2} sx={{ padding: { xs: 0.5, sm: 1 } }}>
                <DataGridHeader
                  onAction={(value, chosenLabels) => handleSearch(value, chosenLabels)}
                  labels={labels}
                />
                <DataGrid
                  style={{
                    marginTop: 0,
                    borderTopWidth: 0.5,
                    borderLeftWidth: 0,
                    borderRightWidth: 0,
                    borderBottomWidth: 0,
                    borderRadius: 0
                  }}
                  autoHeight={true}
                  rows={workoutResult}
                  rowHeight={100}
                  disableColumnMenu={true}
                  rowsPerPageOptions={[20, 40, 80]}
                  pagination={true}
                  initialState={{ pagination: { pageSize: 20 } }}
                  components={{
                    NoRowsOverlay: () => (
                      <Grid container justifyContent="center" alignItems="center" spacing={2} p={5}>
                        <Grid item xs={12} textAlign="center">
                          <Typography variant='body1' alignItems="center">
                            {t('search.notFound')}
                          </Typography>
                        </Grid>
                      </Grid>
                    )
                  }}
                  columns={mdUp
                    ? columns
                    : columns.filter(
                      (column) => (
                        column.field !== 'label'
                      )
                    )
                  }
                  getRowHeight={() => 'auto'}
                  sx={{
                    '&.MuiDataGrid-root .MuiDataGrid-cell': {
                      pb: 1, pt: 1
                    }
                  }}
                />
              </Stack>
            </Card>
          </Stack>
        }
      </ContainerWorkout>
    </Box>
  )
}
